/**
 * Hall Detail Page with Availability Calendar
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { hallAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const HallDetailPage = () => {
  const { slug } = useParams();
  const [hall, setHall] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null); // Track selected floor for multi-floor halls

  // Current month/year for calendar
  const [currentDate, setCurrentDate] = useState(new Date());
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const fetchHallDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hallAPI.getBySlug(slug);
      setHall(response.data);
    } catch (err) {
      console.error('Error fetching hall details:', err);
      setError('Failed to load hall details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchAvailability = useCallback(async (floorId = null) => {
    try {
      // Pass the floor ID if available
      const response = await hallAPI.getAvailability(slug, currentYear, currentMonth, floorId);
      setAvailability(response.data);
    } catch (err) {
      console.error('Error fetching availability:', err);
    }
  }, [slug, currentYear, currentMonth]);

  useEffect(() => {
    fetchHallDetails();
  }, [fetchHallDetails]);

  // Don't set a default floor - start with general view
  // User can select a floor by clicking on floor cards or dropdown

  // Fetch availability when month/year changes OR when floor selection changes
  useEffect(() => {
    fetchAvailability(selectedFloor?.id || null);
  }, [fetchAvailability, selectedFloor]);

  const goToPreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const getDayClass = (day) => {
    if (day.is_past) {
      return 'bg-gray-200 text-gray-400 cursor-not-allowed';
    }
    if (selectedDate === day.date) {
      return 'bg-primary-600 text-white font-bold ring-2 ring-primary-300';
    }

    // If specific floor is selected, show simple available/booked
    if (selectedFloor) {
      if (!day.is_available) {
        return 'bg-red-100 text-red-600 cursor-not-allowed font-semibold';
      }
      return 'bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer font-semibold';
    }

    // For multi-floor halls without specific floor selection, show partial availability
    if (day.total_floors && day.available_floors_count > 0 && day.booked_floors_count > 0) {
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 cursor-pointer font-semibold';
    }
    if (!day.is_available) {
      return 'bg-red-100 text-red-600 cursor-not-allowed font-semibold';
    }
    return 'bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer font-semibold';
  };

  const handleDateClick = (day) => {
    // Only allow clicking if date is actually available
    if (!day.is_past && day.is_available) {
      setSelectedDate(day.date);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading hall details..." />
      </div>
    );
  }

  if (error || !hall) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full">
          <ErrorMessage
            message={error || 'Hall not found'}
            onRetry={fetchHallDetails}
          />
          <div className="text-center mt-4">
            <Link
              to="/"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              ← Back to All Halls
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to All Halls
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Hall Details */}
          <div className="lg:col-span-2">
            {/* Hall Image - Shows selected floor image or default hall image */}
            {selectedFloor && selectedFloor.image ? (
              <img
                src={selectedFloor.image}
                alt={selectedFloor.name}
                className="w-full h-96 object-cover rounded-lg shadow-lg mb-6"
              />
            ) : hall.featured_image ? (
              <img
                src={hall.featured_image}
                alt={hall.name}
                className="w-full h-96 object-cover rounded-lg shadow-lg mb-6"
              />
            ) : (
              <div className="w-full h-96 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg shadow-lg mb-6 flex items-center justify-center">
                <svg className="w-32 h-32 text-white opacity-50" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
              </div>
            )}

            {/* Hall Info Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">{hall.name}</h1>

              <div className="flex items-center text-gray-600 mb-4">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>{hall.address}, {hall.city} - {hall.pincode}</span>
              </div>

              <p className="text-gray-700 text-lg mb-6">{hall.description}</p>

              {/* Floors/Venues Section - for multi-floor halls like Uday Bhavan */}
              {hall.floors && Array.isArray(hall.floors) && hall.floors.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                    Available Venues
                  </h2>
                  <div className="space-y-4">
                    {hall.floors.map((floor, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedFloor(floor)}
                        className={`border-2 rounded-lg p-5 hover:border-primary-400 transition-all cursor-pointer ${
                          selectedFloor?.id === floor.id
                            ? 'border-primary-500 bg-gradient-to-r from-primary-50 to-primary-100 shadow-lg ring-2 ring-primary-300'
                            : 'border-primary-100 bg-gradient-to-r from-white to-primary-50'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-gray-800">{floor.floor_name}</h3>
                            <p className="text-lg text-primary-600 font-semibold">{floor.venue_name}</p>
                          </div>
                          <div className="text-right">
                            <div className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                              {floor.capacity} Guests
                            </div>
                          </div>
                        </div>

                        <p className="text-gray-600 mb-3">{floor.description}</p>

                        <div className="mb-3">
                          <div className="bg-white p-3 rounded border border-gray-200 inline-block">
                            <p className="text-xs text-gray-500 mb-1">Price</p>
                            <p className="text-2xl font-bold text-primary-600">₹{floor.price}</p>
                            <p className="text-xs text-gray-400">per day</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {floor.amenities && floor.amenities.slice(0, 6).map((amenity, i) => (
                            <span key={i} className="inline-block bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded">
                              {amenity}
                            </span>
                          ))}
                          {floor.amenities && floor.amenities.length > 6 && (
                            <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                              +{floor.amenities.length - 6} more
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 mr-2 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    <span className="text-sm text-gray-600">Capacity</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{hall.capacity} guests</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 mr-2 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                    <span className="text-sm text-gray-600">Area</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{hall.area_sqft} sq ft</p>
                </div>
              </div>

              {/* Amenities */}
              {hall.amenities && hall.amenities.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Amenities</h3>
                  <div className="flex flex-wrap gap-2">
                    {hall.amenities.map((amenity, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing */}
              <div className="border-t pt-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Pricing</h3>
                <div className="bg-primary-50 p-6 rounded-lg inline-block">
                  <p className="text-sm text-gray-600 mb-2">Starting from</p>
                  <p className="text-4xl font-bold text-primary-600">₹{hall.celebration_price}</p>
                  <p className="text-sm text-gray-500 mt-2">Per day</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <span className="text-gray-700">{hall.phone}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <span className="text-gray-700">{hall.email}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                      <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                    </svg>
                    <span className="text-gray-700">WhatsApp: {hall.whatsapp}</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                    </svg>
                    <span className="text-gray-700">Contact Person: {hall.contact_person}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Availability Calendar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Check Availability</h2>

              {/* Floor Selection Dropdown (only for multi-floor halls) */}
              {hall && hall.floors && hall.floors.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Venue/Floor
                  </label>
                  <select
                    value={selectedFloor?.id || ''}
                    onChange={(e) => {
                      const floorId = e.target.value;
                      if (floorId === '') {
                        setSelectedFloor(null); // Show general availability
                      } else {
                        const floor = hall.floors.find(f => f.id === parseInt(floorId));
                        setSelectedFloor(floor);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">All Venues (General View)</option>
                    {hall.floors.map((floor) => (
                      <option key={floor.id} value={floor.id}>
                        {floor.floor_name} - {floor.venue_name}
                      </option>
                    ))}
                  </select>
                  {selectedFloor && (
                    <p className="text-xs text-gray-500 mt-1">
                      Capacity: {selectedFloor.capacity} guests • ₹{selectedFloor.price}/day
                    </p>
                  )}
                </div>
              )}

              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-gray-100 rounded-full transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-lg font-semibold text-gray-800">
                  {monthNames[currentMonth - 1]} {currentYear}
                </h3>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-gray-100 rounded-full transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Calendar Grid */}
              {availability && availability.days ? (
                <>
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty cells for days before month starts */}
                    {Array.from({ length: new Date(currentYear, currentMonth - 1, 1).getDay() }).map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square"></div>
                    ))}

                    {/* Actual days */}
                    {availability.days.map((day) => {
                      // Determine title based on availability
                      let title = '';
                      if (day.is_past) {
                        title = 'Past date';
                      } else if (selectedFloor) {
                        // Specific floor selected
                        title = day.is_available ? `${selectedFloor.venue_name} - Available` : `${selectedFloor.venue_name} - Booked`;
                      } else if (day.total_floors && day.available_floors_count !== undefined) {
                        // Multi-floor hall without specific selection
                        if (day.available_floors_count === day.total_floors) {
                          title = `All ${day.total_floors} floors available`;
                        } else if (day.available_floors_count > 0) {
                          title = `${day.available_floors_count} of ${day.total_floors} floors available`;
                        } else {
                          title = 'All floors booked';
                        }
                      } else {
                        title = day.is_available ? 'Available' : 'Booked';
                      }

                      // Show floor count only when no specific floor is selected
                      const showFloorCount = !selectedFloor && day.total_floors && day.available_floors_count !== undefined && !day.is_past;

                      return (
                        <button
                          key={day.date}
                          onClick={() => handleDateClick(day)}
                          disabled={day.is_past || (!day.is_available && (!day.available_floors_count || day.available_floors_count === 0))}
                          className={`aspect-square rounded-lg text-xs flex flex-col items-center justify-center transition-all ${getDayClass(day)}`}
                          title={title}
                        >
                          <span className="font-semibold">{day.day}</span>
                          {showFloorCount && (
                            <span className="text-[0.6rem] leading-none mt-0.5">
                              {day.available_floors_count}/{day.total_floors}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="mt-6 space-y-2">
                    <div className="flex items-center text-sm">
                      <div className="w-4 h-4 bg-green-100 rounded mr-2"></div>
                      <span className="text-gray-700">Fully Available</span>
                    </div>
                    {availability.is_multi_floor && (
                      <div className="flex items-center text-sm">
                        <div className="w-4 h-4 bg-yellow-100 rounded mr-2"></div>
                        <span className="text-gray-700">Partially Booked</span>
                      </div>
                    )}
                    <div className="flex items-center text-sm">
                      <div className="w-4 h-4 bg-red-100 rounded mr-2"></div>
                      <span className="text-gray-700">Fully Booked</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <div className="w-4 h-4 bg-gray-200 rounded mr-2"></div>
                      <span className="text-gray-700">Past Date</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <div className="w-4 h-4 bg-primary-600 rounded mr-2"></div>
                      <span className="text-gray-700">Selected</span>
                    </div>
                  </div>

                  {/* Selected Date Info */}
                  {selectedDate && (() => {
                    const selectedDay = availability.days.find(d => d.date === selectedDate);
                    return (
                      <div className="mt-6 p-4 bg-primary-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Selected Date:</p>
                        <p className="text-lg font-bold text-primary-600">{selectedDate}</p>
                        {selectedFloor ? (
                          // Specific floor selected
                          <div className="mt-2">
                            <p className="text-xs text-gray-600">{selectedFloor.venue_name}</p>
                            {selectedDay && selectedDay.is_available ? (
                              <p className="text-sm text-green-600">✓ Available for booking</p>
                            ) : (
                              <p className="text-sm text-red-600">✗ Already booked</p>
                            )}
                          </div>
                        ) : selectedDay && selectedDay.total_floors && selectedDay.available_floors_count !== undefined ? (
                          // Multi-floor hall without specific selection
                          <div className="mt-2">
                            {selectedDay.available_floors_count === selectedDay.total_floors ? (
                              <p className="text-sm text-green-600">✓ All {selectedDay.total_floors} floors available</p>
                            ) : selectedDay.available_floors_count > 0 ? (
                              <p className="text-sm text-yellow-600">⚠️ {selectedDay.available_floors_count} of {selectedDay.total_floors} floors available</p>
                            ) : (
                              <p className="text-sm text-red-600">✗ All floors booked</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-green-600 mt-2">✓ This date is available</p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Contact Button */}
                  <div className="mt-6">
                    <a
                      href={`https://wa.me/${hall.whatsapp.replace(/\D/g, '')}?text=Hi, I'm interested in booking ${selectedFloor ? selectedFloor.venue_name || selectedFloor.name : hall.name}${selectedDate ? ` for ${selectedDate}` : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-300"
                    >
                      <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                      </svg>
                      Contact via WhatsApp
                    </a>
                    <a
                      href={`tel:${hall.phone}`}
                      className="block w-full mt-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-300"
                    >
                      Call Now: {hall.phone}
                    </a>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <LoadingSpinner text="Loading availability..." />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HallDetailPage;
